// Totem facial (`/`, item 1.6): seleção de sala em `login-page` + reconhecimento com câmera.
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronRight, DoorOpen, ScanLine } from "lucide-react";
import type { TotemRoom } from "@centro-cirurgico/contracts";
import { Brand, Empty } from "@centro-cirurgico/ui";

import { ClinicalFlow } from "../components/ClinicalFlow";
import { ErrorState } from "../components/ErrorState";
import { Header } from "../components/Header";
import { LoadingState } from "../components/LoadingState";
import { hasFacialCapability, useCapabilities } from "../hooks/useCapabilities";
import { storeDeviceMode } from "../components/ModeSwitch";
import { errorMessage, getTotemRooms } from "../utils/api";
import { readDeviceSession } from "../utils/session";

export function TotemPage() {
  const { data: capabilities, loading, error, refetch } = useCapabilities();
  const roomsQuery = useQuery({
    queryKey: ["totem", "rooms"],
    queryFn: async () => (await getTotemRooms()).rooms,
    staleTime: 60_000
  });
  const [selectedRoom, setSelectedRoom] = useState<TotemRoom | null>(null);
  const facialEnabled = hasFacialCapability(capabilities?.capabilities);
  // Aparelho de sala (link preso a uma sala): o totem já abre na sala do link e oferece a
  // volta para a leitura de pulseira no mesmo aparelho.
  const [roomSession] = useState(() => {
    const session = readDeviceSession();
    return session?.mode === "room" ? session : null;
  });
  useEffect(() => {
    if (selectedRoom || !roomSession?.roomId || !roomsQuery.data) return;
    const bound = roomsQuery.data.find((room) => room.id === roomSession.roomId);
    if (bound) setSelectedRoom(bound);
  }, [roomSession, roomsQuery.data, selectedRoom]);

  if (loading || roomsQuery.isPending) {
    return (
      <div className="app">
        <LoadingState message="Preparando totem…" />
      </div>
    );
  }

  if (error || roomsQuery.isError) {
    return (
      <div className="app">
        <ErrorState
          title="Sem conexão com a API"
          message={error?.message || errorMessage(roomsQuery.error, "Não foi possível conectar ao servidor.")}
          onRetry={() => void Promise.all([refetch(), roomsQuery.refetch()])}
        />
      </div>
    );
  }

  if (!facialEnabled) {
    return (
      <div className="app">
        <ErrorState
          title="Reconhecimento facial indisponível"
          message="Este totem precisa do módulo facial ativo para registrar acessos."
          onRetry={() => void refetch()}
          retryLabel="Verificar novamente"
        />
      </div>
    );
  }

  if (!selectedRoom) {
    const rooms = roomsQuery.data ?? [];
    return (
      <div className="login-page totem-setup">
        <header className="login-header">
          <Brand tag="TOTEM DE ACESSO" variant="full" />
          <span className="eyebrow">Configuração do totem</span>
        </header>
        <main className="totem-setup-main">
          <div className="totem-setup-heading">
            <h1>Selecione a sala monitorada</h1>
            <p>A seleção vale até a página ser recarregada. Toque na sala em que este totem está instalado.</p>
          </div>
          {rooms.length === 0 ? (
            <Empty title="Nenhuma sala ativa" icon={<DoorOpen size={32} />}>
              Cadastre e ative as salas no painel administrativo para usá-las no totem.
            </Empty>
          ) : (
            <div className="service-grid" role="list" aria-label="Salas disponíveis">
              {rooms.map((room) => (
                <button
                  key={room.id}
                  type="button"
                  role="listitem"
                  className="service-card"
                  onClick={() => setSelectedRoom(room)}
                >
                  <span className="eyebrow">Sala</span>
                  <h3>{room.name}</h3>
                  <small>
                    {room.unitName} · {room.sectorName}
                  </small>
                  <ChevronRight aria-hidden="true" size={22} />
                </button>
              ))}
            </div>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <Header
        hospital={selectedRoom.unitName}
        sector={selectedRoom.sectorName}
        room={selectedRoom.name}
        capabilities={capabilities}
        actions={
          roomSession ? (
            <Link className="secondary compact" to="/sala" onClick={() => storeDeviceMode("wristband")}>
              <ScanLine aria-hidden="true" size={16} /> Pulseira do paciente
            </Link>
          ) : null
        }
      />
      <main className="app-main">
        <ClinicalFlow room={selectedRoom} />
      </main>
    </div>
  );
}
